import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CircleNotch, FloppyDisk, SealCheck, Gear, LockKey } from "@phosphor-icons/react";

const RESPONSE_TYPES = ["", "shared_data", "factual_form", "document"];

const NumberInput = (props) => (
  <input type="number" {...props}
    className="w-20 px-2 py-1.5 t-support bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:outline-none" />
);

export default function ConfigEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rev, setRev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/gov/config/${id}`); setRev(r.data.revision); }
      catch (e) { toast.error("Could not load revision"); }
      setLoading(false);
    })();
  }, [id]);

  const editable = rev?.status === "draft";
  const setReq = (i, k, v) => setRev((s) => { const rq = [...s.requirements_template]; rq[i] = { ...rq[i], [k]: v }; return { ...s, requirements_template: rq }; });
  const setAg = (i, k, v) => setRev((s) => { const a = [...s.agenda_framework]; a[i] = { ...a[i], [k]: v }; return { ...s, agenda_framework: a }; });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/gov/config/${id}`, {
        requirements_template: rev.requirements_template,
        agenda_framework: rev.agenda_framework,
        operating_instructions: (rev.operating_instructions || []).filter((l) => l.trim()),
        timing_rules: rev.timing_rules,
      });
      toast.success("Draft saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const publish = async () => {
    setBusy(true);
    try { await api.post(`/gov/config/${id}/publish`); toast.success("Published"); navigate("/gov/ops"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    setBusy(false);
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground t-support"><CircleNotch size={18} className="animate-spin text-primary" weight="bold" /> loading…</div>;
  if (!rev) return <div className="t-body">Revision not found.</div>;

  return (
    <div className="max-w-4xl" data-testid="config-editor-page">
      <button onClick={() => navigate("/gov/ops")} className="flex items-center gap-1.5 t-support text-muted-foreground hover:text-foreground mb-3"><ArrowLeft size={14} /> District Operations</button>
      <div className="flex items-center gap-2 eyebrow mb-2"><Gear size={14} weight="fill" className="text-primary" /> Annual GOV configuration</div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="t-page-title">Revision {rev.revision_number}</h1>
        <span className="text-xs uppercase tracking-wide px-2 py-0.5 border rounded border-border text-muted-foreground">{rev.status}</span>
      </div>
      {!editable && (
        <div data-testid="config-readonly-banner" className="mt-3 flex items-center gap-2 border border-border bg-secondary rounded-md px-4 py-2.5 t-support text-muted-foreground">
          <LockKey size={16} weight="duotone" className="text-primary" /> This revision is {rev.status} and immutable. Create a successor draft from District Operations to make changes.
        </div>
      )}

      {/* Requirements */}
      <section className="mt-6">
        <h2 className="t-section-title">Preparation requirements</h2>
        <div className="mt-3 space-y-2" data-testid="req-editor">
          {rev.requirements_template.map((r, i) => (
            <div key={r.requirement_key} data-testid={`req-${r.requirement_key}`} className="border border-border bg-surface rounded-md p-4">
              <p className="t-support font-semibold">{r.display_order}. {r.label}</p>
              <div className="mt-2 grid sm:grid-cols-2 gap-3">
                <label className="block sm:col-span-2"><span className="eyebrow">Guidance</span>
                  <input data-testid={`guidance-${r.requirement_key}`} disabled={!editable} value={r.guidance || ""} onChange={(e) => setReq(i, "guidance", e.target.value)}
                    className="mt-1 w-full px-3 py-2 t-support bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:outline-none disabled:opacity-60" /></label>
                <div className="flex items-center gap-2"><span className="eyebrow">Order</span>
                  <NumberInput data-testid={`order-${r.requirement_key}`} min="1" disabled={!editable} value={r.display_order} onChange={(e) => setReq(i, "display_order", Number(e.target.value))} /></div>
                <div className="flex items-center gap-2"><span className="eyebrow">Response type</span>
                  <select data-testid={`rtype-${r.requirement_key}`} disabled={!editable} value={r.response_type || ""} onChange={(e) => setReq(i, "response_type", e.target.value || null)}
                    className="px-2 py-1.5 t-support bg-background border border-border rounded focus:ring-2 focus:ring-primary focus:outline-none disabled:opacity-60">
                    {RESPONSE_TYPES.map((t) => <option key={t} value={t}>{t || "—"}</option>)}
                  </select></div>
                <div className="flex items-center gap-2"><span className="eyebrow">Weight</span>
                  <NumberInput data-testid={`weight-${r.requirement_key}`} min="0" disabled={!editable} value={r.completion_weight ?? ""} onChange={(e) => setReq(i, "completion_weight", e.target.value === "" ? null : Number(e.target.value))} /></div>
                <label className="flex items-center gap-2"><input data-testid={`required-${r.requirement_key}`} type="checkbox" disabled={!editable} checked={!!r.required} onChange={(e) => setReq(i, "required", e.target.checked)} /> <span className="t-support">Required</span></label>
                <label className="flex items-center gap-2"><input data-testid={`focus-${r.requirement_key}`} type="checkbox" disabled={!editable} checked={!!r.focus_area} onChange={(e) => setReq(i, "focus_area", e.target.checked)} /> <span className="t-support">District focus area</span></label>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Agenda */}
      <section className="mt-8">
        <h2 className="t-section-title">Agenda framework</h2>
        <div className="mt-3 space-y-2" data-testid="agenda-editor">
          {rev.agenda_framework.map((a, i) => (
            <div key={a.key} className="border border-border bg-surface rounded-md p-4">
              <p className="t-support font-semibold">{a.order}. {a.title}</p>
              <div className="mt-2 flex items-center gap-2"><span className="eyebrow">Default duration (min)</span>
                <NumberInput data-testid={`agenda-dur-${a.key}`} disabled={!editable} value={a.default_duration_min ?? ""} onChange={(e) => setAg(i, "default_duration_min", e.target.value === "" ? null : Number(e.target.value))} /></div>
            </div>
          ))}
        </div>
      </section>

      {/* Operating instructions + timing */}
      <section className="mt-8 grid md:grid-cols-2 gap-6">
        <div>
          <h2 className="t-section-title">Operating instructions</h2>
          <textarea data-testid="instructions-editor" disabled={!editable} rows={9}
            value={(rev.operating_instructions || []).join("\n")}
            onChange={(e) => setRev({ ...rev, operating_instructions: e.target.value.split("\n") })}
            className="mt-3 w-full px-3 py-2.5 t-support bg-surface border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none disabled:opacity-60" />
          <p className="mt-1 t-support text-muted-foreground">One instruction per line.</p>
        </div>
        <div>
          <h2 className="t-section-title">Timing rules</h2>
          <div className="mt-3 border border-border bg-surface rounded-md p-4 space-y-3" data-testid="timing-editor">
            <div className="flex items-center justify-between gap-2"><span className="t-support">Reconfirm — days before</span>
              <NumberInput data-testid="timing-reconfirm" disabled={!editable} value={rev.timing_rules?.reconfirm_days_before ?? ""} onChange={(e) => setRev({ ...rev, timing_rules: { ...rev.timing_rules, reconfirm_days_before: Number(e.target.value) } })} /></div>
            <div className="flex items-center justify-between gap-2"><span className="t-support">Agenda to AG — days before</span>
              <NumberInput data-testid="timing-agenda" disabled={!editable} value={rev.timing_rules?.agenda_to_ag_days_before ?? ""} onChange={(e) => setRev({ ...rev, timing_rules: { ...rev.timing_rules, agenda_to_ag_days_before: Number(e.target.value) } })} /></div>
          </div>
        </div>
      </section>

      {editable && (
        <div className="mt-8 flex flex-wrap gap-3">
          <button data-testid="save-config" onClick={save} disabled={busy}
            className="inline-flex items-center gap-2 border border-border px-6 py-3 t-support font-semibold rounded-md hover:bg-secondary disabled:opacity-60">
            {busy ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <FloppyDisk size={16} weight="bold" />} Save draft</button>
          <button data-testid="publish-config" onClick={publish} disabled={busy}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 t-support font-semibold rounded-md hover:bg-primary/90 active:scale-95 disabled:opacity-60">
            <SealCheck size={16} weight="bold" /> Publish revision</button>
        </div>
      )}
    </div>
  );
}
